import { type FC } from "react";
import { userIsLoggedIn } from "~/domains/user/utils/roles";
import PageTitle from "~/common/components/PageTitle/v1/PageTitle";
import useMediaQuery from "~/common/hooks/useMediaQuery";
import { tileBuilder } from "~/features/myAccount/dashboard/components/TileBuilder";
import { getMyCompanyTiles } from "~/features/myAccount/dashboard/sections/myCompanyTiles";
import { userCanSeeMyCompanySettings } from "~/features/myAccount/dashboard/helper/permissions";
import { getMyAccountTiles } from "~/features/myAccount/dashboard/sections/myAccountTiles";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "~/common/utils/screenSizes";
import { useTranslation } from "react-i18next";
import { styled } from "~/common/mui";
import type { TileData } from "~/features/myAccount/dashboard/type";

interface AccountSettingsPageViewProps {
  loggedIn: boolean;
  isDesktop: boolean;
  myAccountTiles: TileData[];
  myCompanyTiles: TileData[];
  canSeeMyCompanySettings: boolean;
}

export const AccountSettingsPageView: FC<AccountSettingsPageViewProps> = ({
  loggedIn,
  isDesktop,
  myAccountTiles,
  myCompanyTiles,
  canSeeMyCompanySettings,
}) => {
  const { t } = useTranslation();

  return loggedIn ? (
    <AccountSettingsRoot className={isDesktop ? "desktop" : "mobile"}>
      <PageTitle title={t("page.accountSettings.title")} />
      <div className="scha__account-settings__main">
        {myAccountTiles.length > 0 && (
          <div className="scha__account-settings__section">
            <div className="scha__account-settings__section__my-account">
              <h3 className="scha__account-settings__section__title headline-small mb-3">
                {t("page.accountSettings.section.myAccount.title")}
              </h3>
              <div className="scha__account-settings__section__tiles">{tileBuilder(myAccountTiles)}</div>
            </div>
          </div>
        )}
        {canSeeMyCompanySettings && myCompanyTiles.length > 0 && (
          <div className="scha__account-settings__section">
            <div className="scha__account-settings__section__my-company">
              <h3 className="scha__account-settings__section__title headline-small mt-0 mb-3">
                {t("page.accountSettings.section.myCompany.title")}
              </h3>
              <div className="scha__account-settings__section__tiles">{tileBuilder(myCompanyTiles)}</div>
            </div>
          </div>
        )}
      </div>
    </AccountSettingsRoot>
  ) : (
    <></>
  );
};

export const AccountSettingsPage: FC = () => {
  const loggedIn = userIsLoggedIn();
  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);
  const myCompanyTiles = getMyCompanyTiles();
  const myAccountTiles = getMyAccountTiles();

  return (
    <AccountSettingsPageView
      loggedIn={loggedIn}
      isDesktop={isDesktop}
      myAccountTiles={myAccountTiles}
      myCompanyTiles={myCompanyTiles}
      canSeeMyCompanySettings={userCanSeeMyCompanySettings()}
    />
  );
};

const AccountSettingsRoot = styled("div")(({ theme }) => ({
  "& .scha__account-settings__section__tiles": {
    marginRight: theme.spacing(-4),
    display: "flex",
    flexWrap: "wrap",
    alignItems: "stretch",
  },
  "& .scha__account-settings__section__tile": {
    marginBottom: theme.spacing(4),
    marginRight: theme.spacing(4),
    flex: "0 1 calc(25% - 32px)",
    "& .scha__lc__card": {
      height: "100%",
    },
  },
  "&.mobile .scha__account-settings__section__tiles": {
    marginBottom: 0,
  },
  "&.mobile .scha__account-settings__section__tile": {
    flex: "0 1 100%",
    marginBottom: theme.spacing(2.5),
  },
}));

export function createAccountSettings() {
  return <AccountSettingsPage />;
}
